/* oxlint-disable sorare/enforce-monetary-reward-disclaimer -- Non-reward currency (wallet/market/JONUM limits/etc.) — disclaimer does not apply. */
import type { ReactNode, Ref } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'styled-components';
import styled, { css } from 'styled-components';

import type { Currency, FiatCurrency } from '#src/__generated__/globalTypes.ts';
import {
  Cryptocurrency,
  SupportedCurrency,
} from '#src/__generated__/globalTypes.ts';
import { IconButton } from '#src/atoms/buttons/IconButton/index.tsx';
import { FontAwesomeIcon } from '#src/atoms/icons/index.tsx';
import { iconRepeat } from '#src/atoms/icons/system/iconRepeat.ts';
import { DecimalInput } from '#src/atoms/inputs/DecimalInput/index.tsx';
import { Horizontal, Vertical } from '#src/atoms/layout/flex/index.tsx';
import { LabelM } from '#src/atoms/typography/index.tsx';
// oxlint-disable-next-line sorare/no-restricted-paths
import { useIntlContext } from '#src/contexts/intl/index.ts';
import { useCryptoAmount } from '#src/hooks/useCryptoAmount.ts';
import { useMonetaryAmount } from '#src/hooks/useMonetaryAmount.ts';
import { cryptocurrencySymbols } from '#src/lib/cryptocurrency.ts';
import { fiatCurrencySymbols } from '#src/lib/fiat.ts';
import { MonetaryAmount } from '#src/lib/monetaryAmount.ts';

import { useCryptoDisplay } from './useCryptoDisplay.ts';

const Label = styled.label<{
  $error?: boolean;
}>`
  position: relative;
  max-width: 100%;
  margin: auto;
  text-align: center;
  display: flex;
  cursor: text;
  font: var(--input-font, var(--t-bold) var(--t-48));
  color: var(--color);
  z-index: 0;
  ${({ $error }) =>
    $error
      ? css`
          --color: var(--c-red-400);
        `
      : css`
          --color: var(--input-color, var(--c-blue-400));
        `};
  &:before {
    content: attr(data-before);
    margin-right: var(--unit);
    display: block;
    visibility: hidden;
  }
  &:after {
    content: attr(data-after);
    margin-left: var(--unit);
    display: block;
    visibility: hidden;
  }
`;

const Placeholder = styled.span`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  color: transparent;
  &:before {
    content: attr(data-before);
    margin-right: var(--unit);
    display: block;
    color: var(--color);
  }
  &:after {
    content: attr(data-after);
    display: block;
    margin-left: var(--unit);
    color: var(--color);
  }
`;

const PlaceholderAmount = styled.span<{ background?: boolean }>`
  ${({ background }) =>
    background
      ? css`
          padding: var(--unit) var(--unit);
          border-radius: var(--double-unit);
          background-color: var(--c-nd-200);
          z-index: -1;
        `
      : css`
          visibility: hidden;
        `}
`;

const StyledInput = styled(DecimalInput)`
  width: 100%;
  display: inline-flex;
  text-align: center;
  font: inherit;
  border: none;
  background-color: transparent;
  color: var(--color);
  line-height: 100%;
`;

type CenteredInputProps = {
  value: string;
  symbol: string;
  symbolBefore?: string;
  placeholder?: string;
  disabled?: boolean;
  ariaLabel: string;
  error?: boolean;
  onChange: (newValue: string, newFormattedValue: number) => void;
  autoFocus?: boolean;
  background?: boolean;
  max: number;
  min: number;
  step: string;
  ref?: Ref<HTMLInputElement>;
};

const CenteredInputWithSymbol = ({
  value,
  symbol,
  symbolBefore,
  placeholder,
  disabled,
  ariaLabel,
  onChange,
  error,
  autoFocus,
  background,
  max,
  min,
  step,
  ref,
}: CenteredInputProps) => (
  <Label data-before={symbolBefore} data-after={symbol} $error={error}>
    <StyledInput
      ref={ref}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      disabled={disabled}
      ariaLabel={ariaLabel}
      autoFocus={autoFocus}
      max={max}
      min={min}
      step={step}
    />
    <Placeholder data-before={symbolBefore} data-after={symbol}>
      <PlaceholderAmount background={background}>
        {value || placeholder}
      </PlaceholderAmount>
    </Placeholder>
  </Label>
);

export type Props = {
  minMonetaryAmount?: MonetaryAmount;
  cryptocurrency: Cryptocurrency;
  defaultValue: MonetaryAmount;
  fiatCurrency: FiatCurrency;
  defaultCurrency?: Currency;
  onlyShowFiatCurrency: boolean;
  onChange: (
    monetaryAmount: MonetaryAmount,
    target: HTMLInputElement,
    referenceCurrency: SupportedCurrency
  ) => void;
  cryptoDisplay?: boolean;
  onCryptoDisplayChange?: (cryptoDisplay: boolean) => void;
  placeholder?: string;
  error?: ReactNode;
  disabled?: boolean;
  setOutBidCallback?: (callback: (amount: MonetaryAmount) => void) => void;
  autoFocus?: boolean;
  useSecondaryInput?: boolean;
};

export const MonetaryInput = ({
  cryptocurrency,
  defaultValue,
  cryptoDisplay: controlledCryptoDisplay,
  fiatCurrency,
  onChange,
  defaultCurrency,
  placeholder,
  onCryptoDisplayChange,
  setOutBidCallback,
  onlyShowFiatCurrency,
  error,
  disabled,
  autoFocus = true,
  useSecondaryInput = false,
  minMonetaryAmount = MonetaryAmount.zero,
}: Props) => {
  const { formatNumber } = useIntlContext();
  const [monetaryAmount, setMonetaryAmount] =
    useState<MonetaryAmount>(defaultValue);
  const { toMonetaryAmountInReferenceCurrency } = useMonetaryAmount();

  const {
    formatCryptoAmount,
    toSubUnitAmount,
    getCryptoAmount,
    maximumFractionDigits,
  } = useCryptoAmount(cryptocurrency);
  const ref = useRef<HTMLInputElement>(null);

  const cryptoInputProps = (() => {
    switch (cryptocurrency) {
      case Cryptocurrency.LAMPORT: {
        return {
          step: '0.001',
          max: 5000,
          symbol: 'SOL',
          symbolBefore: '',
          min: minMonetaryAmount.sol,
        };
      }
      case Cryptocurrency.WEI:
      default: {
        return {
          step: '0.0001',
          max: 5000,
          symbol: 'ETH',
          symbolBefore: '',
          min: minMonetaryAmount.eth,
        };
      }
    }
  })();

  const inputProps = (cryptoDisplay: boolean) => {
    if (cryptoDisplay) {
      return cryptoInputProps;
    }
    return {
      step: '0.01',
      max: 2147483647,
      symbol: '',
      symbolBefore: fiatCurrencySymbols[fiatCurrency],
      min: minMonetaryAmount.toMajorUnit(fiatCurrency),
    };
  };

  const [cryptoDisplay, toggleCryptoDisplay] = useCryptoDisplay({
    onlyShowFiatCurrency,
    defaultCurrency,
    controlledCryptoDisplay,
    onCryptoDisplayChange,
  });

  const cryptoAmountStr = formatCryptoAmount(
    monetaryAmount.cryptoSubUnit(cryptocurrency),
    undefined,
    {
      maximumFractionDigits,
      useGrouping: false,
    },
    {
      hideSymbol: true,
    }
  );

  const cryptoAmountWithCurrency = formatCryptoAmount(
    monetaryAmount.cryptoSubUnit(cryptocurrency),
    undefined,
    {
      maximumFractionDigits,
    }
  );

  const fiatAmountStr = formatNumber(monetaryAmount.toMajorUnit(fiatCurrency), {
    maximumFractionDigits: 2,
    useGrouping: false,
  });
  const fiatAmountWithCurrency = formatNumber(
    monetaryAmount.toMajorUnit(fiatCurrency),
    {
      maximumFractionDigits: 2,
      style: 'currency',
      currency: fiatCurrency,
    }
  );
  const [input, setInput] = useState<string>(
    cryptoDisplay ? cryptoAmountStr : fiatAmountStr
  );

  const [secondaryInput, setSecondaryInput] = useState<string>(
    !cryptoDisplay ? cryptoAmountStr : fiatAmountStr
  );

  const [prevCryptoDisplay, setPrevCryptoDisplay] = useState(cryptoDisplay);
  if (prevCryptoDisplay !== cryptoDisplay) {
    setPrevCryptoDisplay(cryptoDisplay);
    setInput(cryptoDisplay ? cryptoAmountStr : fiatAmountStr);
    setSecondaryInput(cryptoDisplay ? fiatAmountStr : cryptoAmountStr);
  }

  const handle = useCallback(
    (value: string, newFormattedValue: number, isSecondaryInput?: boolean) => {
      const inputIsCrypto = isSecondaryInput ? !cryptoDisplay : cryptoDisplay;
      if (isSecondaryInput) {
        setSecondaryInput(value);
      } else {
        setInput(value);
      }

      const referenceCurrency = inputIsCrypto
        ? cryptocurrency
        : SupportedCurrency[fiatCurrency];
      const amount = inputIsCrypto
        ? toSubUnitAmount(newFormattedValue)
        : Math.round(newFormattedValue * 100);
      const newMonetaryAmount = toMonetaryAmountInReferenceCurrency(
        amount,
        referenceCurrency
      );

      setMonetaryAmount(newMonetaryAmount);
      onChange(
        newMonetaryAmount,
        ref.current!,
        inputIsCrypto ? cryptocurrency : SupportedCurrency[fiatCurrency]
      );
      const convertedAmount = inputIsCrypto
        ? newMonetaryAmount.toMajorUnit(fiatCurrency).toString()
        : newMonetaryAmount.crypto(cryptocurrency).toString();
      if (isSecondaryInput) {
        setInput(convertedAmount);
      } else {
        setSecondaryInput(convertedAmount);
      }
    },
    [
      cryptoDisplay,
      cryptocurrency,
      fiatCurrency,
      onChange,
      toMonetaryAmountInReferenceCurrency,
      toSubUnitAmount,
    ]
  );

  const handler =
    (isSecondaryInput: boolean) =>
    (newValue: string, newFormattedValue: number) => {
      handle(newValue, newFormattedValue, isSecondaryInput);
    };

  useEffect(() => {
    if (setOutBidCallback) {
      setOutBidCallback(() => {
        return (amount: MonetaryAmount) => {
          const newAmount = cryptoDisplay
            ? amount.crypto(cryptocurrency)
            : amount.toMajorUnit(fiatCurrency);
          handle(newAmount.toString(), newAmount);
        };
      });
    }
  }, [
    cryptoDisplay,
    cryptocurrency,
    handle,
    setOutBidCallback,
    fiatCurrency,
    getCryptoAmount,
  ]);

  return (
    <Vertical gap={1} center>
      <Vertical gap={0} center>
        <CenteredInputWithSymbol
          ref={ref}
          value={input}
          onChange={handler(false)}
          placeholder={placeholder}
          ariaLabel={cryptoDisplay ? 'crypto' : 'fiat'}
          disabled={disabled}
          error={!!error}
          autoFocus={autoFocus}
          {...inputProps(cryptoDisplay)}
        />
        {error}
      </Vertical>
      {!onlyShowFiatCurrency && (
        <Horizontal>
          {useSecondaryInput ? (
            <div
              style={
                {
                  '--input-font': '16px',
                  '--input-color': 'var(--c-nd-700)',
                } as CSSProperties
              }
            >
              <CenteredInputWithSymbol
                onChange={handler(true)}
                value={secondaryInput}
                placeholder="0"
                ariaLabel="secondaryInput"
                background
                {...inputProps(!cryptoDisplay)}
                symbolBefore="≈"
                symbol={
                  cryptoDisplay
                    ? fiatCurrencySymbols[fiatCurrency]
                    : cryptocurrencySymbols[cryptocurrency]
                }
              />
            </div>
          ) : (
            <>
              <LabelM color="var(--c-nd-700)">
                ≈&nbsp;
                {cryptoDisplay
                  ? fiatAmountWithCurrency
                  : cryptoAmountWithCurrency}
              </LabelM>
              <IconButton
                size="compact"
                color="tertiary"
                aria-label="switch"
                onClick={toggleCryptoDisplay}
                disableDebounce
              >
                <FontAwesomeIcon icon={iconRepeat} size="xs" />
              </IconButton>
            </>
          )}
        </Horizontal>
      )}
    </Vertical>
  );
};
